import { FileCheck2, PackageCheck, ShieldCheck } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Overline, Reveal } from "@/components/Reveal";
import { CtaBand } from "@/components/CtaBand";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { HSN_NOTE, PORTS } from "@/data/site";

const specRows = [
    ["Crude Protein", "36–38% minimum, typical"],
    ["Moisture", "10–12% maximum, typical"],
    ["Sand & Silica", "2% maximum"],
    ["Packing", "50 kg PP/HDPE export bags"],
    ["Container Load", "22–24 MT per 20ft container"],
    ["Origin", "Rajasthan, India — partner processor network (incl. Niwai sourcing hub)"],
];

const uses = [
    "Protein-rich raw material in compound cattle feed formulations",
    "Poultry and livestock feed ingredient for feed mills and integrators",
    "Cost-effective protein source alongside or in place of soybean meal",
    "Raw material for feed manufacturers, importers, and trading houses",
];

const qualityPoints = [
    {
        icon: FileCheck2,
        title: "Batch-wise COA",
        text: "A Certificate of Analysis is coordinated for every batch, with lab test support available on request.",
    },
    {
        icon: ShieldCheck,
        title: "Third-party Inspection",
        text: "SGS, Geo-Chem, or equivalent inspection can be arranged at loading on buyer request.",
    },
    {
        icon: PackageCheck,
        title: "Supervised Stuffing",
        text: "Container stuffing is coordinated and documented, with photos shared before dispatch.",
    },
];

const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "Rapeseed/Mustard De-Oiled Cake (DOC)",
    alternateName: ["Rapeseed-Mustard Meal", "Mustard DOC", "Rapeseed DOC", "Mustard/Rapeseed Oil Cake"],
    description:
        "High-protein animal feed raw material. Crude protein 36–38% min typical, moisture 10–12% max typical, sand & silica 2% max. Packed in 50 kg PP/HDPE export bags, 22–24 MT per 20ft container. Origin: Rajasthan, India.",
    brand: { "@type": "Organization", name: "AXORA PS GLOBAL LLP" },
    countryOfOrigin: "IN",
    category: "Animal Feed Raw Material",
    additionalProperty: [
        { "@type": "PropertyValue", name: "Crude Protein", value: "36–38% minimum typical" },
        { "@type": "PropertyValue", name: "Moisture", value: "10–12% maximum typical" },
        { "@type": "PropertyValue", name: "Sand & Silica", value: "2% maximum" },
    ],
};

export default function DocSpecs() {
    return (
        <>
            <Seo
                title="Rapeseed/Mustard DOC Specifications — Protein 36–38% | AXORA PS GLOBAL LLP"
                description="Full export specifications for rapeseed/mustard de-oiled cake (DOC): protein 36–38% min, moisture 10–12% max, sand & silica 2% max, 50 kg bags, 22–24 MT per 20ft container, FOB/CFR/CIF."
                path="/products/rapeseed-mustard-doc"
                ogImage="/images/og-rapeseed-mustard-doc.jpg"
                jsonLd={productJsonLd}
            />

            <section data-testid="docspecs-hero" className="relative overflow-hidden bg-white pt-[72px]">
                <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[420px] bg-gradient-to-b from-steel-light/80 via-steel-light/30 to-white" />
                <div className="relative mx-auto w-full max-w-7xl px-5 pb-16 pt-14 sm:px-8 lg:px-12 lg:pb-20 lg:pt-20">
                    <Reveal>
                        <Overline>DOC Specifications</Overline>
                        <h1 className="mt-4 max-w-3xl font-display text-4xl font-bold leading-[1.08] tracking-tight text-navy sm:text-5xl">
                            Rapeseed/Mustard DOC Specifications
                        </h1>
                        <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
                            Rapeseed/Mustard De-Oiled Cake — also called Rapeseed-Mustard Meal, Mustard DOC, Rapeseed
                            DOC, or Mustard/Rapeseed Oil Cake — is the protein-rich residue of oilseed crushing,
                            exported as a high-protein animal feed raw material.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section data-testid="docspecs-uses" className="border-t border-slate-200 bg-white py-20 lg:py-24">
                <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-12 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
                    <Reveal className="lg:col-span-5">
                        <Overline>Application</Overline>
                        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy">Uses in Animal Feed</h2>
                        <p className="mt-4 text-base leading-relaxed text-slate-600">
                            Mustard DOC is an established protein ingredient in compound feed across Asia, the Middle
                            East, and Africa, valued for its protein content, availability, and landed cost.
                        </p>
                        <img
                            src="/images/doc-own-heap.jpg"
                            alt="Heap of granular mustard de-oiled cake — actual DOC feed material supplied by AXORA PS GLOBAL LLP"
                            loading="lazy"
                            className="mt-8 aspect-[4/3] w-full rounded-2xl border border-slate-200 object-cover"
                        />
                    </Reveal>
                    <div className="lg:col-span-7">
                        <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            {uses.map((use, i) => (
                                <Reveal key={use} delay={i * 0.08}>
                                    <li className="flex h-full items-start gap-3 rounded-xl border border-slate-200 bg-white p-5 text-sm font-medium leading-relaxed text-navy/85">
                                        <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-amber" />
                                        {use}
                                    </li>
                                </Reveal>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            <section data-testid="docspecs-table" className="bg-mist py-20 lg:py-24">
                <div className="mx-auto w-full max-w-7xl px-5 sm:px-8 lg:px-12">
                    <Reveal>
                        <Overline>Specification</Overline>
                        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy">Full Specification Table</h2>
                    </Reveal>
                    <Reveal delay={0.12} className="mt-10">
                        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                            <table className="w-full text-left" data-testid="spec-table">
                                <thead>
                                    <tr className="bg-navy text-white">
                                        <th scope="col" className="px-6 py-4 font-display text-xs font-bold uppercase tracking-[0.18em] sm:px-8">
                                            Parameter
                                        </th>
                                        <th scope="col" className="px-6 py-4 font-display text-xs font-bold uppercase tracking-[0.18em] sm:px-8">
                                            Typical Specification
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {specRows.map(([param, value], i) => (
                                        <tr key={param} className={`border-t border-slate-100 ${i % 2 === 0 ? "bg-white" : "bg-mist/60"}`}>
                                            <th scope="row" className="px-6 py-4 text-sm font-semibold text-navy sm:px-8">
                                                {param}
                                            </th>
                                            <td className="px-6 py-4 text-sm text-slate-600 sm:px-8">{value}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Reveal>
                    <Reveal delay={0.18} className="mt-8">
                        <div className="flex flex-wrap items-center gap-3" data-testid="hsn-codes">
                            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">HSN Codes:</span>
                            <span className="rounded-md bg-navy px-3 py-1.5 font-mono text-sm font-semibold text-white">23069012</span>
                            <span className="rounded-md bg-navy px-3 py-1.5 font-mono text-sm font-semibold text-white">23069021</span>
                            <span className="rounded-md bg-navy px-3 py-1.5 font-mono text-sm font-semibold text-white">23069090</span>
                        </div>
                        <p className="mt-3 max-w-3xl text-xs leading-relaxed text-slate-500">{HSN_NOTE}</p>
                    </Reveal>
                </div>
            </section>

            <section data-testid="docspecs-packing" className="bg-white py-20 lg:py-24">
                <div className="mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-12 px-5 sm:px-8 lg:grid-cols-2 lg:px-12">
                    <Reveal>
                        <div className="relative">
                            <div aria-hidden="true" className="absolute -left-4 -top-4 h-full w-full rounded-2xl border border-steel/40" />
                            <img
                                src="/images/containers-stack.jpg"
                                alt="Stacked intermodal shipping containers at a terminal — DOC is loaded at 22–24 MT per 20ft container"
                                loading="lazy"
                                className="relative aspect-[4/3] w-full rounded-2xl object-cover"
                            />
                        </div>
                    </Reveal>
                    <Reveal delay={0.12}>
                        <Overline>Logistics</Overline>
                        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy">Packing & Container Loading</h2>
                        <p className="mt-5 text-base leading-relaxed text-slate-600">
                            DOC is packed in 50 kg PP/HDPE export bags suitable for long sea transit. Standard loading
                            is 22–24 MT per 20ft container, with stuffing supervised and photo-documented before the
                            container is sealed.
                        </p>
                        <div className="mt-7">
                            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Shipments routed through</p>
                            <div className="mt-3 flex flex-wrap gap-2.5">
                                {PORTS.map((port) => (
                                    <span key={port} className="rounded-full border border-steel/30 bg-steel-light px-3.5 py-1.5 text-xs font-semibold text-navy">
                                        {port}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <p className="mt-6 text-sm leading-relaxed text-slate-600">
                            Shipment terms: FOB, CFR, or CIF. Payment terms: LC / TT, subject to final contract.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section data-testid="docspecs-quality" className="border-t border-slate-200 bg-white py-20 lg:py-24">
                <div className="mx-auto w-full max-w-7xl px-5 sm:px-8 lg:px-12">
                    <Reveal>
                        <Overline>Quality Assurance</Overline>
                        <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-navy">Quality Inspection & COA Support</h2>
                    </Reveal>
                    <Reveal delay={0.08} className="mt-10">
                        <img
                            src="/images/doc-own-sample.jpg"
                            alt="Mustard DOC batch sample spread on white paper for quality review — actual product from AXORA PS GLOBAL LLP"
                            loading="lazy"
                            className="mx-auto aspect-[4/3] w-full max-w-3xl rounded-2xl border border-slate-200 object-cover"
                        />
                    </Reveal>
                    <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">
                        {qualityPoints.map((point, i) => (
                            <Reveal key={point.title} delay={i * 0.1}>
                                <div className="h-full rounded-xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(10,37,64,0.1)]">
                                    <point.icon className="h-7 w-7 text-steel" />
                                    <h3 className="mt-5 font-display text-lg font-bold tracking-tight text-navy">{point.title}</h3>
                                    <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{point.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.15} className="mt-12">
                        <div className="flex flex-wrap items-center gap-4 rounded-2xl border border-amber/40 bg-amber/5 p-6 sm:p-8">
                            <p className="flex-1 text-sm font-medium leading-relaxed text-navy md:text-base">
                                Need the current batch COA, lab reports, or a loading-slot quote? Share your
                                destination port and quantity — a co-founder responds directly.
                            </p>
                            <WhatsAppButton label="Request Quote on WhatsApp" location="docspecs-inline" />
                        </div>
                    </Reveal>
                </div>
            </section>

            <CtaBand location="docspecs" />
        </>
    );
}
